"use client";

import { Pause, Play } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { mediaUrlFromAsset } from "@/lib/media";
import { cn } from "@/lib/utils";
import type { CommentVoiceNote } from "@/validations/comment";

function formatDuration(durationMs: number) {
  const seconds = Math.max(0, Math.round(durationMs / 1_000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function normalizeWaveform(input: number[]) {
  const count = 34;
  if (input.length === 0) {
    return Array.from(
      { length: count },
      (_, index) => 20 + ((index * 19) % 48),
    );
  }
  if (input.length <= count) return input;
  return Array.from({ length: count }, (_, index) => {
    const start = Math.floor((index * input.length) / count);
    const end = Math.max(
      start + 1,
      Math.floor(((index + 1) * input.length) / count),
    );
    return Math.max(...input.slice(start, end));
  });
}

export function CommentVoiceNotePlayer({ voice }: { voice: CommentVoiceNote }) {
  const src = mediaUrlFromAsset(voice.asset);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progressMs, setProgressMs] = useState(0);
  const bars = useMemo(
    () => normalizeWaveform(voice.waveform),
    [voice.waveform],
  );
  const playedBars =
    voice.durationMs > 0
      ? Math.round((progressMs / voice.durationMs) * bars.length)
      : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const update = () => setProgressMs(audio.currentTime * 1_000);
    const play = () => setIsPlaying(true);
    const pause = () => setIsPlaying(false);
    const ended = () => {
      setIsPlaying(false);
      setProgressMs(0);
    };
    audio.addEventListener("timeupdate", update);
    audio.addEventListener("play", play);
    audio.addEventListener("pause", pause);
    audio.addEventListener("ended", ended);
    return () => {
      audio.removeEventListener("timeupdate", update);
      audio.removeEventListener("play", play);
      audio.removeEventListener("pause", pause);
      audio.removeEventListener("ended", ended);
    };
  }, []);

  if (!src) return null;

  return (
    <div className="mt-2 flex max-w-sm items-center gap-3 rounded-2xl border border-border bg-surface-alt px-3 py-2.5">
      <audio ref={audioRef} src={src} preload="metadata" className="hidden">
        <track kind="captions" />
      </audio>
      <button
        type="button"
        onClick={() => {
          const audio = audioRef.current;
          if (!audio) return;
          if (audio.paused) void audio.play();
          else audio.pause();
        }}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-text-primary text-background"
        aria-label={isPlaying ? "Pause voice comment" : "Play voice comment"}
      >
        {isPlaying ? (
          <Pause className="h-4 w-4 fill-current" />
        ) : (
          <Play className="ml-0.5 h-4 w-4 fill-current" />
        )}
      </button>
      <div
        className="flex min-w-0 flex-1 items-center gap-0.5"
        aria-hidden="true"
      >
        {bars.map((bar, index) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: waveform buckets are positional.
            key={index}
            className={cn(
              "w-0.5 shrink-0 rounded-full",
              index < playedBars ? "bg-brand" : "bg-text-secondary/55",
            )}
            style={{ height: `${Math.max(4, Math.round((bar / 100) * 25))}px` }}
          />
        ))}
      </div>
      <span className="shrink-0 text-xs text-text-secondary tabular-nums">
        {formatDuration(isPlaying ? progressMs : voice.durationMs)}
      </span>
    </div>
  );
}
